/**
 * H5P / Genially / Moodle H5P (mod_h5pactivity) — detecção e normalização de embeds.
 * Genially não usa caminhos /h5p/; a lógica de resizer oficial (h5p.org) fica separada do Moodle.
 */
import { getEffectiveH5pUrl } from '../config/mediaDefaults.js';

const ORG_H5P_RESIZER = 'https://h5p.org/sites/all/modules/h5p/library/js/h5p-resizer.js';
const ORG_H5P_RESIZER_SCRIPT = `<script src="${ORG_H5P_RESIZER}" charset="UTF-8"></script>`;

/** Parâmetros opcionais do core Moodle (h5p/embed.php) que reduzem moldura/botões do player H5P. */
const MOODLE_EMBED_PHP_MINIMAL_KEYS = ['frame', 'export', 'embed', 'copyright'];

/** H5P oficial (CDN) — usar h5p-resizer.js da h5p.org */
export function contentUsesOfficialH5pOrgResizer({ url = '', embedCode = '' } = {}) {
  const u = String(url).toLowerCase();
  const e = String(embedCode).toLowerCase();
  return u.includes('h5p.org') || u.includes('h5p.com') || e.includes('h5p.org') || e.includes('h5p.com');
}

/**
 * Conteúdo que deve receber tratamento H5P (altura / resizer), sem confundir com Genially.
 */
export function contentLooksLikeH5P({ url = '', embedCode = '' } = {}) {
  if (contentUsesOfficialH5pOrgResizer({ url, embedCode })) return true;
  const u = String(url).toLowerCase();
  const e = String(embedCode).toLowerCase();
  if (u.includes('/h5p/embed') || u.includes('h5p/embed.php')) return true;
  if (e.includes('/h5p/embed') || e.includes('h5p/embed.php') || e.includes('mod_h5pactivity')) return true;
  return false;
}

const PUBLIC_EMBED_HOST_SUFFIXES = ['h5p.org', 'h5p.com', 'genially.com'];

function hostIsPublicEmbedProvider(hostname) {
  const h = String(hostname || '').toLowerCase();
  return PUBLIC_EMBED_HOST_SUFFIXES.some((suffix) => h === suffix || h.endsWith(`.${suffix}`));
}

/** URLs públicas de exemplo — não exibir aviso de login Moodle no builder. */
function isPublicDemoH5pUrl(src) {
  if (!src || typeof src !== 'string') return false;
  const s = src.trim();
  return /h5p\.org\/h5p\/embed\/707/i.test(s);
}

/**
 * Embeds copiados do Moodle (H5P autohospedado ou Genially via URL do LMS) exigem sessão
 * e não renderizam no palco do Geppetto; no export/import no Moodle funcionam normalmente.
 */
export function embedLikelyRequiresMoodleLoginInBuilder({ url = '', embedCode = '' } = {}) {
  const sources = [];
  const trimmedUrl = String(url).trim();
  const trimmedEmbed = String(embedCode).trim();
  if (trimmedUrl) sources.push(trimmedUrl);
  const iframeSrc = extractFirstIframeSrc(trimmedEmbed);
  if (iframeSrc) sources.push(iframeSrc);
  if (!sources.length) return false;
  if (sources.every(isPublicDemoH5pUrl)) return false;

  for (const src of sources) {
    if (isPublicDemoH5pUrl(src)) continue;
    if (isSelfHostedH5pIframePath(src)) return true;
    if (contentUsesOfficialH5pOrgResizer({ url: src, embedCode: '' })) continue;
    try {
      const { hostname } = new URL(src.trim());
      if (!hostIsPublicEmbedProvider(hostname)) return true;
    } catch {
      return true;
    }
  }
  return false;
}

/** Iframe H5P hospedado no próprio Moodle (ou outro host com embed.php + pacote). */
export function isSelfHostedH5pIframePath(src) {
  if (!src || typeof src !== 'string') return false;
  try {
    const url = new URL(src.trim());
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    const path = url.pathname.toLowerCase();
    const q = url.search.toLowerCase();
    return path.includes('/h5p/embed') || path.includes('h5p/embed.php') || q.includes('mod_h5pactivity');
  } catch {
    return false;
  }
}

/**
 * Acrescenta na URL do core Moodle /h5p/embed.php os parâmetros que desativam moldura/export/embed/copyright
 * do player (stdClass em embed.php). Não altera h5p.org nem outros hosts.
 */
export function appendMoodleEmbedPhpMinimalParams(src) {
  if (!src || typeof src !== 'string') return src;
  try {
    const u = new URL(src.trim());
    const pl = u.pathname.toLowerCase();
    if (!pl.includes('/h5p/') || !pl.includes('embed.php')) return src;
    MOODLE_EMBED_PHP_MINIMAL_KEYS.forEach((k) => {
      if (!u.searchParams.has(k)) u.searchParams.set(k, '0');
    });
    return u.toString();
  } catch {
    return src;
  }
}

function applyMoodleMinimalParamsToAllIframeSrcs(html) {
  return html.replace(/src=(["'])([^"']+)\1/gi, (full, q, srcVal) => {
    const next = appendMoodleEmbedPhpMinimalParams(srcVal);
    if (next === srcVal) return full;
    return `src=${q}${next}${q}`;
  });
}

/**
 * Remove divs/toolbars colados junto do Moodle e mantém só o iframe H5P (+ script resizer será tratado depois).
 * Evita lixo HTML da página da atividade no export e reduz “camadas” extras ao lado do player.
 */
export function simplifyMoodleH5pEmbedToBareIframe(html) {
  if (!html || typeof html !== 'string') return html;

  if (typeof document !== 'undefined') {
    try {
      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const candidates = [...wrap.querySelectorAll('iframe')];
      const moodleIframe = candidates.find((el) => isSelfHostedH5pIframePath(el.getAttribute('src') || ''));
      if (!moodleIframe) return html;
      const src = appendMoodleEmbedPhpMinimalParams(moodleIframe.getAttribute('src') || '');
      moodleIframe.setAttribute('src', src);
      const out = document.createElement('div');
      out.appendChild(moodleIframe.cloneNode(true));
      return out.innerHTML;
    } catch {
      /* fallthrough */
    }
  }

  if (!/h5p\/embed\.php|mod_h5pactivity/i.test(html)) return html;
  const im = html.match(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/i);
  if (!im) return html;
  let iframeHtml = im[0];
  const sm = iframeHtml.match(/src=(["'])([^"']+)\1/i);
  if (!sm || !isSelfHostedH5pIframePath(sm[2])) return html;
  const quote = sm[1];
  const newSrc = appendMoodleEmbedPhpMinimalParams(sm[2]);
  iframeHtml = iframeHtml.replace(/src=(["'])([^"']+)\1/i, () => `src=${quote}${newSrc}${quote}`);
  return iframeHtml;
}

export function extractFirstIframeSrc(html) {
  const m = String(html).match(/<iframe[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : '';
}

/** URL do h5p-resizer.js colada no embed (Moodle ou outro). */
export function extractH5pResizerScriptSrc(embedCode) {
  const m = String(embedCode).match(/<script[^>]+src=["']([^"']*h5p-resizer\.js[^"']*)["']/i);
  return m ? m[1].trim() : null;
}

export function stripH5pResizerScriptsFromHtml(html) {
  if (!html || typeof html !== 'string') return html;
  return html
    .replace(/<script\b[^>]*h5p-resizer\.js[^>]*>\s*<\/script>/gi, '')
    .replace(/<script\b[^>]*h5p-resizer\.js[^>]*\/>/gi, '');
}

/** Garante iframe fluido — Moodle cola width fixo (ex.: 1163px) que estoura no Visualizar. */
function mergeEmbedIframeInlineStyle(style, extraChunks) {
  let next = String(style || '').replace(/\s*;\s*;/g, ';').trim();
  extraChunks.forEach((chunk) => {
    const prop = chunk.split(':')[0].trim();
    if (new RegExp(`${prop.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i').test(next)) return;
    next = (next ? next.replace(/;?\s*$/, '') + '; ' : '') + chunk;
  });
  return next.replace(/\s*;\s*;/g, ';').trim();
}

function applyResponsiveEmbedIframeSizing(iframe) {
  iframe.setAttribute('width', '100%');
  let style = iframe.getAttribute('style') || '';
  style = style.replace(/\bwidth\s*:\s*[^;]+;?/gi, '').replace(/\s*;\s*;/g, ';').trim();
  style = mergeEmbedIframeInlineStyle(style, [
    'width:100%',
    'max-width:100%',
    'display:block',
    'box-sizing:border-box',
  ]);
  iframe.setAttribute('style', style);
}

function applyResponsiveEmbedIframeAttrsRegex(attrs) {
  let a = String(attrs || '').replace(/\bwidth\s*=\s*["'][^"']*["']/gi, '');
  if (!/\bwidth\s*=/i.test(a)) a += ' width="100%"';
  else a = a.replace(/\bwidth\s*=\s*["'][^"']*["']/i, 'width="100%"');

  if (/style\s*=\s*["']([^"']*)["']/i.test(a)) {
    a = a.replace(/style\s*=\s*["']([^"']*)["']/i, (_, s) => {
      let ns = String(s).replace(/\bwidth\s*:\s*[^;]+;?/gi, '').replace(/\s*;\s*;/g, ';').trim();
      ns = mergeEmbedIframeInlineStyle(ns, ['width:100%', 'max-width:100%', 'display:block', 'box-sizing:border-box']);
      return `style="${ns}"`;
    });
  } else {
    a = `${a.trim()} style="width:100%;max-width:100%;display:block;box-sizing:border-box"`;
  }
  return a;
}

export function wrapH5pEmbedHtml(innerHtml) {
  const body = String(innerHtml || '').trim();
  if (!body) return '';
  return `<div class="h5p-embed-wrapper" style="width:100%;max-width:100%;border-radius:8px;overflow:hidden">${body}</div>`;
}

function normalizeH5pIframesWithDom(html) {
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  wrap.querySelectorAll('iframe').forEach((iframe) => {
    let src = iframe.getAttribute('src') || '';
    src = appendMoodleEmbedPhpMinimalParams(src);
    iframe.setAttribute('src', src);
    applyResponsiveEmbedIframeSizing(iframe);
    if (!isSelfHostedH5pIframePath(src) && !contentUsesOfficialH5pOrgResizer({ url: src, embedCode: '' })) return;
    let style = iframe.getAttribute('style') || '';
    style = style.replace(/height\s*:\s*0(px)?\s*;?/gi, '').replace(/\s*;\s*;/g, ';').trim();
    if (!/min-height/i.test(style)) {
      style = (style ? style.replace(/;?\s*$/, '') + '; ' : '') + 'min-height: 400px';
    }
    iframe.setAttribute('style', style);
    const h = iframe.getAttribute('height');
    if (h === '0' || h === '0px') iframe.setAttribute('height', '400');
  });
  return wrap.innerHTML;
}

function normalizeH5pIframesRegex(html) {
  let out = html.replace(/<iframe([^>]*)>/gi, (full, attrs) => {
    let a = applyResponsiveEmbedIframeAttrsRegex(attrs);
    if (!/h5p\/embed|embed\.php|mod_h5pactivity|h5p\.org|h5p\.com/i.test(a)) return `<iframe${a}>`;
    if (/style\s*=\s*["']([^"']*)["']/i.test(a)) {
      a = a.replace(/style\s*=\s*["']([^"']*)["']/i, (_, s) => {
        let ns = String(s).replace(/height\s*:\s*0(px)?\s*;?/gi, '').replace(/\s*;\s*;/g, ';').trim();
        if (!/min-height/i.test(ns)) ns = (ns ? ns.replace(/;?\s*$/, '') + '; ' : '') + 'min-height:400px';
        return `style="${ns}"`;
      });
    } else {
      a = `${a.trim()} style="min-height:400px"`;
    }
    a = a.replace(/\bheight\s*=\s*["']0["']/i, 'height="400"');
    a = a.replace(/\bheight\s*=\s*["']0px["']/i, 'height="400"');
    return `<iframe${a}>`;
  });
  out = applyMoodleMinimalParamsToAllIframeSrcs(out);
  return out;
}

/** Remove altura 0 do iframe Moodle (H5P player) para o conteúdo aparecer antes do resizer rodar. */
export function normalizeH5pIframeEmbedHtml(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof document !== 'undefined') {
    try {
      return normalizeH5pIframesWithDom(html);
    } catch {
      /* fallthrough */
    }
  }
  return normalizeH5pIframesRegex(html);
}

/**
 * Infere URL do resizer do Moodle a partir do embed ou do src do iframe (versão em /h5p/h5plib/vNNN/ quando presente no HTML).
 */
export function inferMoodleH5pResizerUrl(embedOrIframeSrc) {
  const blob = String(embedOrIframeSrc || '');
  const vm = blob.match(/\/h5p\/h5plib\/(v\d+)\//i);
  const iframeSrc = extractFirstIframeSrc(blob) || blob;
  if (!isSelfHostedH5pIframePath(iframeSrc)) return null;
  try {
    const origin = new URL(iframeSrc.trim()).origin;
    const ver = vm ? vm[1] : 'v127';
    return `${origin}/h5p/h5plib/${ver}/joubel/core/js/h5p-resizer.js`;
  } catch {
    return null;
  }
}

export function resolveH5pResizerScriptSrc({ url = '', embedCode = '' } = {}) {
  const pasted = extractH5pResizerScriptSrc(embedCode);
  if (pasted) return pasted;
  if (contentUsesOfficialH5pOrgResizer({ url, embedCode })) return ORG_H5P_RESIZER;
  const iframeHint = extractFirstIframeSrc(embedCode) || String(url).trim();
  if (iframeHint && isSelfHostedH5pIframePath(iframeHint)) {
    return inferMoodleH5pResizerUrl(embedCode || iframeHint);
  }
  return null;
}

/** HTML + um único script resizer para export (ZIP / Moodle). */
export function buildH5pExportInnerHtml({ url: h5pUrlRaw, embedCode: h5pEmbed } = {}) {
  const trimmedEmbed = (h5pEmbed || '').trim();
  const trimmedUrl = (h5pUrlRaw || '').trim();
  const looksH5p = contentLooksLikeH5P({ url: trimmedUrl, embedCode: trimmedEmbed });

  let resizerScript = '';
  const pastedSrc = extractH5pResizerScriptSrc(trimmedEmbed);
  if (pastedSrc) {
    const esc = pastedSrc.replace(/"/g, '&quot;');
    resizerScript = `<script src="${esc}" charset="UTF-8"></script>`;
  } else if (contentUsesOfficialH5pOrgResizer({ url: trimmedUrl, embedCode: trimmedEmbed }) && looksH5p) {
    resizerScript = ORG_H5P_RESIZER_SCRIPT;
  } else if (looksH5p) {
    const inferred = inferMoodleH5pResizerUrl(trimmedEmbed || trimmedUrl);
    if (inferred) {
      const esc = inferred.replace(/"/g, '&quot;');
      resizerScript = `<script src="${esc}" charset="UTF-8"></script>`;
    }
  }

  if (trimmedEmbed && trimmedEmbed.includes('<iframe')) {
    const bare = simplifyMoodleH5pEmbedToBareIframe(trimmedEmbed);
    const body = normalizeH5pIframeEmbedHtml(stripH5pResizerScriptsFromHtml(bare));
    return wrapH5pEmbedHtml(body) + resizerScript;
  }
  if (trimmedUrl !== '') {
    let h5pUrl = getEffectiveH5pUrl(trimmedUrl);
    h5pUrl = appendMoodleEmbedPhpMinimalParams(h5pUrl);
    const inner = `<iframe src="${h5pUrl.replace(/"/g, '&quot;')}" width="100%" height="400" frameborder="0" allow="fullscreen; geolocation *; microphone *; camera *; midi *; encrypted-media *" title="Embed" style="min-height:400px;width:100%;max-width:100%;border:none;box-sizing:border-box"></iframe>`;
    return wrapH5pEmbedHtml(inner) + resizerScript;
  }
  return '';
}

/** HTML só do iframe(s) para preview (scripts removidos — o resizer é injetado via useEffect). */
export function buildH5pPreviewEmbedHtml(embedCode) {
  const trimmed = (embedCode || '').trim();
  if (!trimmed) return '';
  const bare = simplifyMoodleH5pEmbedToBareIframe(trimmed);
  return normalizeH5pIframeEmbedHtml(stripH5pResizerScriptsFromHtml(bare));
}
